import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'
import { Checkbox } from '@/common/ui/checkbox'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Badge } from '@/common/ui/badge'
import { PromotionSelector } from '@/modules/messaging/components/PromotionSelector'
import BrokeFlexMessagePreview from '../BrokeFlexMessagePreview'
import type { PresetOption } from '../BrokeFlexPresetSelector'
import useMutationCreateFromPreset from '../../api/hooks/useMutationCreateFromPreset'
import { X } from 'lucide-react'

interface BrokeFlexPresetConfigFormProps {
	preset: PresetOption
	onSuccess: () => void
	onCancel: () => void
	editMode?: boolean
	campaignId?: string
	initialData?: any
}

const createSchemaForPreset = (preset: PresetOption) => {
	const baseSchema: Record<string, z.ZodTypeAny> = {
		name: z.string().min(1, 'กรุณากรอกชื่อแคมเปญ'),
		message: z.string().min(1, 'กรุณากรอกข้อความ'),
	}

	if (preset.requiredFields.includes('promotionIds')) {
		baseSchema.promotionIds = z
			.array(z.string())
			.min(1, 'กรุณาเลือกโปรโมชั่นอย่างน้อย 1 รายการ')
	} else if (preset.optionalFields.includes('promotionIds')) {
		baseSchema.promotionIds = z.array(z.string()).optional()
	}

	if (preset.requiredFields.includes('couponCodes')) {
		baseSchema.couponCodes = z
			.array(z.string())
			.min(1, 'กรุณากรอกคูปองอย่างน้อย 1 รายการ')
	} else if (preset.optionalFields.includes('couponCodes')) {
		baseSchema.couponCodes = z.array(z.string()).optional()
	}

	if (preset.optionalFields.includes('includeReturnLoss')) {
		baseSchema.includeReturnLoss = z.boolean().optional()
	}

	if (preset.optionalFields.includes('includeCommission')) {
		baseSchema.includeCommission = z.boolean().optional()
	}

	if (preset.optionalFields.includes('includeInviteFriend')) {
		baseSchema.includeInviteFriend = z.boolean().optional()
	}

	return z.object(baseSchema)
}

export default function BrokeFlexPresetConfigForm({
	preset,
	onSuccess,
	onCancel,
	editMode = false,
}: BrokeFlexPresetConfigFormProps) {
	const schema = createSchemaForPreset(preset)
	type FormValues = z.infer<typeof schema>

	const form = useForm<FormValues>({
		resolver: zodResolver(schema),
		defaultValues: {
			name: '',
			message: 'คุณยังมีโอกาสรับโบนัสพิเศษอีกมากมาย! อย่าพลาดโอกาสดีๆ นี้ ',
			promotionIds: [],
			couponCodes: [],
			includeReturnLoss: false,
			includeCommission: false,
			includeInviteFriend: false,
		} as FormValues,
	})

	const createMutation = useMutationCreateFromPreset({
		onSuccess: () => {
			toast.success('สร้างแคมเปญสำเร็จ')
			onSuccess()
		},
		onError: () => {
			toast.error('เกิดข้อผิดพลาดในการสร้างแคมเปญ')
		},
	})

	const handleSubmit = (data: FormValues) => {
		createMutation.mutate({
			preset: preset.preset,
			message: data.message,
			...data,
			isActive: false, // Create as inactive by default
		})
	}

	const showPromotionSelector =
		preset.requiredFields.includes('promotionIds') ||
		preset.optionalFields.includes('promotionIds')

	const showCouponInput =
		preset.requiredFields.includes('couponCodes') ||
		preset.optionalFields.includes('couponCodes')

	const showBonusOptions =
		preset.optionalFields.includes('includeReturnLoss') ||
		preset.optionalFields.includes('includeCommission') ||
		preset.optionalFields.includes('includeInviteFriend')

	return (
		<div className="space-y-8">
			{/* Stage 1 - Main Configuration */}
			<div className="grid gap-6 lg:grid-cols-2">
				{/* Form */}
				<Card>
					<CardHeader>
						<div className="flex items-start justify-between">
							<div>
								<CardTitle className="flex items-center gap-2">
									{preset.name}
									<Badge variant="outline">ระยะที่ 1</Badge>
								</CardTitle>
								<p className="mt-2 text-sm text-muted-foreground">
									{preset.description}
								</p>
							</div>
							{!editMode && (
								<Button type="button" variant="ghost" onClick={onCancel}>
									เปลี่ยนรูปแบบ
								</Button>
							)}
						</div>
					</CardHeader>
					<CardContent>
						<Form {...form}>
							<form
								onSubmit={form.handleSubmit(handleSubmit)}
								className="space-y-6"
							>
								<FormField
									control={form.control}
									name="name"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>ชื่อแคมเปญ</FormLabel>
											<FormControl>
												<Input
													{...field}
													placeholder="กรอกชื่อแคมเปญเพื่อง่ายต่อการจัดการ"
												/>
											</FormControl>
											<FormDescription>
												ชื่อแคมเปญที่ใช้สำหรับระบุและจัดการแคมเปญนี้
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="message"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>ข้อความหัวข้อ</FormLabel>
											<FormControl>
												<Textarea
													{...field}
													placeholder="กรอกข้อความหัวข้อที่ต้องการแสดง"
													rows={2}
												/>
											</FormControl>
											<FormDescription>
												ข้อความหัวข้อที่จะแสดงด้านบนของการ์ด (พื้นหลังสีแดง)
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>

								{showPromotionSelector && (
									<FormField
										control={form.control}
										name="promotionIds"
										render={({ field }) => {
											const isSinglePromotion = preset.defaultMaxItems === 1
											const selectedPromoId =
												field.value && field.value.length > 0
													? field.value[0]
													: ''

											return (
												<FormItem>
													<FormLabel
														required={preset.requiredFields.includes(
															'promotionIds',
														)}
													>
														โปรโมชั่น
														{isSinglePromotion && (
															<Badge variant="outline" className="ml-2 text-xs">
																เลือกได้ 1 รายการ
															</Badge>
														)}
														{!isSinglePromotion && (
															<Badge variant="outline" className="ml-2 text-xs">
																เลือกได้หลายรายการ
															</Badge>
														)}
													</FormLabel>
													<div className="space-y-3">
														<div className="rounded-lg border border-blue-200 p-3">
															<p className="text-sm">
																<span className="font-semibold">
																	💡 คำแนะนำ:
																</span>{' '}
																โปรโมชั่นควรเป็นแบบ <strong>แคมเปญ</strong>{' '}
																เพื่อให้จัดการได้ง่ายและตรวจสอบสิทธิ์ผู้เล่นได้แม่นยำ
															</p>
														</div>

														{isSinglePromotion ? (
															// Single selection for SIMPLE_PROMOTION
															<PromotionSelector
																value={selectedPromoId || undefined}
																onChange={(promotionId) => {
																	field.onChange([promotionId])
																}}
																filterInstantClaimOnly={false}
															/>
														) : (
															// Multiple selection for other presets
															<>
																<PromotionSelector
																	value=""
																	onChange={(promotionId) => {
																		const current = field.value || []
																		if (!current.includes(promotionId)) {
																			field.onChange([...current, promotionId])
																		}
																	}}
																	filterInstantClaimOnly={false}
																/>
																{field.value && field.value.length > 0 && (
																	<div className="flex flex-wrap gap-2">
																		{field.value.map((id: string) => (
																			<Badge
																				key={id}
																				variant="secondary"
																				className="gap-1"
																			>
																				โปรโมชั่น
																				<button
																					type="button"
																					onClick={() => {
																						field.onChange(
																							field.value?.filter(
																								(v: string) => v !== id,
																							) || [],
																						)
																					}}
																					className="ml-1 hover:text-destructive"
																				>
																					<X className="h-3 w-3" />
																				</button>
																			</Badge>
																		))}
																	</div>
																)}
															</>
														)}
													</div>
													<FormDescription>
														{isSinglePromotion
															? 'เลือกโปรโมชั่น 1 รายการที่ต้องการแสดง'
															: 'เลือกโปรโมชั่นที่ต้องการแสดงให้ผู้เล่น (แนะนำให้ใช้โปรโมชั่นแบบ แคมเปญ)'}
													</FormDescription>
													<FormMessage />
												</FormItem>
											)
										}}
									/>
								)}

								{showCouponInput && (
									<FormField
										control={form.control}
										name="couponCodes"
										render={({ field }) => (
											<FormItem>
												<FormLabel
													required={preset.requiredFields.includes(
														'couponCodes',
													)}
												>
													รหัสคูปอง
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														value={(field.value || []).join(', ')}
														onChange={(e) =>
															field.onChange(
																e.target.value
																	.split(',')
																	.map((s) => s.trim())
																	.filter(Boolean),
															)
														}
														placeholder="VIP100, NEWBIE50"
													/>
												</FormControl>
												<FormDescription>
													กรอกรหัสคูปองคั่นด้วยเครื่องหมายจุลภาค (,)
												</FormDescription>
												<FormMessage />
											</FormItem>
										)}
									/>
								)}

								{showBonusOptions && (
									<div className="space-y-4">
										<FormLabel>โบนัสเพิ่มเติม</FormLabel>
										<div className="space-y-3 rounded-lg border p-4">
											{preset.optionalFields.includes('includeReturnLoss') && (
												<FormField
													control={form.control}
													name="includeReturnLoss"
													render={({ field }) => (
														<FormItem className="flex items-center space-x-3 space-y-0">
															<FormControl>
																<Checkbox
																	checked={field.value}
																	onCheckedChange={field.onChange}
																/>
															</FormControl>
															<div className="flex-1">
																<FormLabel className="font-normal">
																	💰 แสดงยอดคืนยอดเสีย
																</FormLabel>
																<FormDescription className="text-xs">
																	แสดงเมื่อมียอดคืนยอดเสียในกระเป๋า
																</FormDescription>
															</div>
														</FormItem>
													)}
												/>
											)}

											{preset.optionalFields.includes('includeCommission') && (
												<FormField
													control={form.control}
													name="includeCommission"
													render={({ field }) => (
														<FormItem className="flex items-center space-x-3 space-y-0">
															<FormControl>
																<Checkbox
																	checked={field.value}
																	onCheckedChange={field.onChange}
																/>
															</FormControl>
															<div className="flex-1">
																<FormLabel className="font-normal">
																	💎 แสดงค่าคอมมิชชั่น
																</FormLabel>
																<FormDescription className="text-xs">
																	แสดงเมื่อมีค่าคอมมิชชั่นในกระเป๋า
																</FormDescription>
															</div>
														</FormItem>
													)}
												/>
											)}

											{preset.optionalFields.includes(
												'includeInviteFriend',
											) && (
												<FormField
													control={form.control}
													name="includeInviteFriend"
													render={({ field }) => (
														<FormItem className="flex items-center space-x-3 space-y-0">
															<FormControl>
																<Checkbox
																	checked={field.value}
																	onCheckedChange={field.onChange}
																/>
															</FormControl>
															<div className="flex-1">
																<FormLabel className="font-normal">
																	👥 แสดงโบนัสชวนเพื่อน
																</FormLabel>
																<FormDescription className="text-xs">
																	แสดงเมื่อมีโบนัสชวนเพื่อนในกระเป๋า
																</FormDescription>
															</div>
														</FormItem>
													)}
												/>
											)}
										</div>
									</div>
								)}

								<div className="flex justify-end gap-3">
									<Button type="button" variant="outline" onClick={onCancel}>
										ยกเลิก
									</Button>
									<Button type="submit" disabled={createMutation.isPending}>
										{createMutation.isPending
											? editMode
												? 'กำลังบันทึก...'
												: 'กำลังสร้าง...'
											: editMode
												? 'บันทึกการแก้ไข'
												: 'สร้างแคมเปญ'}
									</Button>
								</div>
							</form>
						</Form>
					</CardContent>
				</Card>

				{/* Live Preview */}
				<div className="lg:sticky lg:top-6 lg:self-start">
					<BrokeFlexMessagePreview
						message={form.watch('message')}
						presetName={preset.name}
					/>
				</div>
			</div>
		</div>
	)
}
